import React, { useState } from "react";
import {
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import MyButton from "../Button/my-button";
import useLeaveImport from "../../hooks/use-leave-import";

function LeaveImportModal({ open, onClose }) {
  const queryClient = useQueryClient();
  const { importLeaveBalance, downloadTemplate } = useLeaveImport();
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await downloadTemplate();
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setIsLoading(true);
    try {
      const data = await importLeaveBalance(file);
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ["leaveEmployees"] });
      toast.success(
        `${data.succeeded} row${data.succeeded !== 1 ? "s" : ""} imported successfully`
      );
    } catch (error) {
      const message =
        error.response?.data?.errorMessage ||
        "Import failed. Please try again.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    queryClient.invalidateQueries({ queryKey: ["leaveEmployees"] });
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="w-full max-w-lg mx-auto mt-20 bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-lg font-semibold mb-4">Import Leave Balance</h2>
        <p className="text-sm text-gray-500 mb-4">
          Upload the HR Excel spreadsheet (.xlsx). Each row sets the leave
          balance directly for that employee. Re-uploading the same file is
          safe.
        </p>

        <div className="flex justify-end mb-3">
          <button
            onClick={handleDownloadTemplate}
            disabled={isDownloading}
            className="text-sm text-red-900 underline hover:text-red-700 disabled:opacity-50"
          >
            {isDownloading ? "Downloading..." : "⬇ Download Template"}
          </button>
        </div>

        <input
          type="file"
          accept=".xlsx"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-700 border border-gray-300 rounded px-3 py-2 mb-4"
        />

        {result && (
          <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200 text-sm">
            <p>
              <b>Processed:</b> {result.processed} &nbsp;|&nbsp;
              <b>Succeeded:</b>{" "}
              <span className="text-green-600">{result.succeeded}</span>
              &nbsp;|&nbsp;
              <b>Skipped:</b>{" "}
              <span className="text-yellow-600">{result.skipped}</span>
              &nbsp;|&nbsp;
              <b>Errors:</b>{" "}
              <span className="text-red-600">{result.errors.length}</span>
            </p>
          </div>
        )}

        {result?.errors?.length > 0 && (
          <div className="mb-4 max-h-48 overflow-y-auto">
            <p className="text-sm font-semibold text-red-600 mb-2">
              Rows with errors:
            </p>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Employee No.
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.errors.map((err, index) => (
                    <TableRow key={index}>
                      <TableCell>{err.row}</TableCell>
                      <TableCell>{err.employeeNo || "—"}</TableCell>
                      <TableCell>{err.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-2">
          <MyButton onClick={handleClose}>Cancel</MyButton>
          <button
            onClick={handleSubmit}
            disabled={!file || isLoading}
            className="bg-red-900 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && <CircularProgress size={16} color="inherit" />}
            {isLoading ? "Importing..." : "Import"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default LeaveImportModal;
